import React from "react";

import {
  formatFractionDisplay,
  formatFractionInput,
  useGetFields,
  useGetPracticeCrops,
  useGetPracticeMineralFertilizers,
  useGetPracticeOrganicFertilizers,
  useGetPracticePlantProtections,
  useGetPracticeSeedings,
  useGetPracticeTillages,
  useParams,
  useTranslation,
} from "utils";

import { CropFootprintReturnBody, FARM_FOOTPRINTING_DRYING_ENERGY_TYPE_SUFFIXES, FootprintingCropFormType, PRACTICE_SLUG } from "models";

import { Card, ListCard, PageSpin } from "components";

import { ReviewPageTitle } from "pages/app/footprinting/components/ReviewPageTitle";

export const FootprintEditCropDetailsCard = ({
  cropFootprintData,
  isCropFootprintDataLoading,
  setShowEditModal,
  setCropModalForm,
}: {
  cropFootprintData: CropFootprintReturnBody;
  isCropFootprintDataLoading: boolean;
  setShowEditModal: (show: boolean) => void;
  setCropModalForm: (formType: FootprintingCropFormType) => void;
}) => {
  const { t } = useTranslation(["footprinting", "practices"]);
  const { farmId } = useParams();
  const allCropsQuery = useGetPracticeCrops({ enabled: true, isAdmin: true });

  const tillagePracticesQuery = useGetPracticeTillages({ enabled: true });
  const seedingPracticesQuery = useGetPracticeSeedings({ enabled: true });
  const mineralFertilizerPracticesQuery = useGetPracticeMineralFertilizers({ enabled: true });
  const organicFertilizerPracticesQuery = useGetPracticeOrganicFertilizers({ enabled: true });
  const plantProtectionPracticesQuery = useGetPracticePlantProtections();
  const fieldsQuery = useGetFields({ farmId });

  const displayRadioAnswer = (isActive: boolean | null) => (isActive === null ? "" : t(`radioAnswer.${isActive}`));

  if (isCropFootprintDataLoading) return <PageSpin />;

  const handleEditForm = (formType: Exclude<FootprintingCropFormType, null>) => {
    setShowEditModal(true);
    setCropModalForm(formType);
  };

  return (
    <Card className="admin-edit-crop-details-card padding-125">
      <ReviewPageTitle
        title={`${t("review.crop.title", { cropName: allCropsQuery.data?.find((c) => c.id === cropFootprintData.cropId)?.name })} - ${cropFootprintData.year}`}
        iconName="klim/crops-matured"
      />
      {/* Crop general form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        title={t("navigation.crop.general")}
        titleButton={{ label: t("review.edit"), onClick: () => handleEditForm("general") }}
        dataPairs={[
          { label: t("cropGeneralForm.question.totalWeightTonsPerHa"), value: `${cropFootprintData.footprint?.totalWeightTonsPerHa} t/ha` },
          {
            label: t("cropGeneralForm.question.preDryingMoistureContentPct"),
            value: `${formatFractionInput(cropFootprintData.footprint?.preDryingMoistureContentPct ?? 0)} %`,
          },
          { label: t("cropGeneralForm.question.byproductPct"), value: `${formatFractionInput(cropFootprintData?.footprint?.byproductPct ?? 0)} %` },
          { label: t("cropGeneralForm.question.seedWeightKgPerHa"), value: `${cropFootprintData?.footprint?.seedWeightKgPerHa} kg/ha` },
          { label: t("cropGeneralForm.question.soilPh"), value: `${cropFootprintData?.footprint?.soilPh} pH` },
        ]}
      />
      {/* Crop fields form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        title={t("navigation.crop.fields")}
        titleButton={{ label: t("review.edit"), onClick: () => handleEditForm("fields") }}
        dataPairs={cropFootprintData?.footprint?.fieldIds.map((fieldId) => {
          const field = fieldsQuery.data?.find((f) => f.id === fieldId);

          return { label: field?.name ?? "", value: `${field?.size ?? 0} ha` };
        })}
      />
      {/* Crop tillage and seeding form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        title={t("navigation.crop.tillageAndSeeding")}
        titleButton={{ label: t("review.edit"), onClick: () => handleEditForm("tillageAndSeeding") }}
        dataPairs={[
          {
            label: t("cropTillageSeedingForm.tillage.question"),
            value: displayRadioAnswer(cropFootprintData?.footprint?.tillageActive ?? null),
            children: cropFootprintData?.footprint?.tillageActive
              ? cropFootprintData?.footprint?.tillageUsage.map((i, index) => (
                  <ListCard
                    key={index}
                    isSublist
                    title={t("cropTillageSeedingForm.tillage.count") + (index + 1)}
                    dataPairs={[
                      {
                        label: t("cropTillageSeedingForm.tillage.type"),
                        value: t(`${PRACTICE_SLUG.TILLAGES}.${tillagePracticesQuery.data?.find((p) => p.id === i.applicationId)?.translationSlug}`, {
                          ns: "practices",
                        }),
                      },
                      {
                        label: t("cropTillageSeedingForm.tillage.depth"),
                        value: `${i.tillageDepth} cm`,
                      },
                      {
                        label: t("cropTillageSeedingForm.question.implementedOn"),
                        value: `${i.implementedOnHa} ha`,
                      },
                    ]}
                  />
                ))
              : null,
          },
          {
            label: t("cropTillageSeedingForm.seeding.question"),
            value: displayRadioAnswer(cropFootprintData?.footprint?.seedingActive ?? null),
            children: cropFootprintData?.footprint?.seedingActive
              ? cropFootprintData?.footprint?.seedingUsage.map((i, index) => (
                  <ListCard
                    key={index}
                    isSublist
                    title={t("cropTillageSeedingForm.seeding.count") + (index + 1)}
                    dataPairs={[
                      {
                        label: t("cropTillageSeedingForm.seeding.type"),
                        value: t(`${PRACTICE_SLUG.SEEDINGS}.${seedingPracticesQuery.data?.find((p) => p.id === i.applicationId)?.translationSlug}`, {
                          ns: "practices",
                        }),
                      },
                      {
                        label: t("cropTillageSeedingForm.question.implementedOn"),
                        value: `${i.implementedOnHa} ha`,
                      },
                    ]}
                  />
                ))
              : null,
          },
        ]}
      />
      {/* Crop fertilizers form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        title={t("navigation.crop.fertilizers")}
        titleButton={{ label: t("review.edit"), onClick: () => handleEditForm("fertilizers") }}
        dataPairs={[
          {
            label: t("cropFertilizerForm.synthetic.question"),
            value: displayRadioAnswer(cropFootprintData?.footprint?.mineralFertilizerActive ?? null),
            children: cropFootprintData?.footprint?.mineralFertilizerActive
              ? cropFootprintData?.footprint?.mineralFertilizerUsage.map((i, index) => (
                  <ListCard
                    key={index}
                    isSublist
                    title={t("cropFertilizerForm.application.title") + (index + 1)}
                    dataPairs={[
                      {
                        label: t("cropFertilizerForm.application.productName"),
                        value: t(
                          `${PRACTICE_SLUG.MINERAL_FERTILIZERS}.${mineralFertilizerPracticesQuery.data?.find((p) => p.id === i.applicationId)
                            ?.translationSlug}`,
                          { ns: "practices" }
                        ),
                      },
                      {
                        label: t("cropFertilizerForm.application.amount"),
                        value: `${i.amount} ${i.amountUnit}`,
                      },
                    ]}
                  />
                ))
              : null,
          },
          {
            label: t("cropFertilizerForm.organic.question"),
            value: displayRadioAnswer(cropFootprintData?.footprint?.organicFertilizerActive ?? null),
            children: cropFootprintData?.footprint?.organicFertilizerActive
              ? cropFootprintData?.footprint?.organicFertilizerUsage.map((i, index) => (
                  <ListCard
                    key={index}
                    isSublist
                    title={t("cropFertilizerForm.application.title") + (index + 1)}
                    dataPairs={[
                      {
                        label: t("cropFertilizerForm.application.productName"),
                        value: t(
                          `${PRACTICE_SLUG.ORGANIC_FERTILIZERS}.${organicFertilizerPracticesQuery.data?.find((p) => p.id === i.applicationId)
                            ?.translationSlug}`,
                          { ns: "practices" }
                        ),
                      },
                      {
                        label: t("cropFertilizerForm.application.nitrogen"),
                        value: `${i.nValue} %`,
                      },
                      {
                        label: t("cropFertilizerForm.application.amount"),
                        value: `${i.amount} ${i.amountUnit}`,
                      },
                    ]}
                  />
                ))
              : null,
          },
          { label: t("cropFertilizerForm.application.lime"), value: `${cropFootprintData?.footprint?.limeT} tonnes` },
        ]}
      />
      {/* Crop plant protection form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        title={t("navigation.crop.plantProtection")}
        titleButton={{ label: t("review.edit"), onClick: () => handleEditForm("plantProtection") }}
        dataPairs={[
          {
            label: t("cropPlantProtectionForm.question.plantProtectionQuestionLabel"),
            value: displayRadioAnswer(cropFootprintData?.footprint?.plantProtectionActive ?? null),
            children: cropFootprintData?.footprint?.plantProtectionActive
              ? cropFootprintData?.footprint?.plantProtectionUsage.map((i, index) => (
                  <ListCard
                    key={index}
                    isSublist
                    title={t("cropPlantProtectionForm.question.applicationNumber", { count: index + 1 })}
                    dataPairs={[
                      {
                        label: t("cropPlantProtectionForm.question.plantProtectionTypeQuestionLabel"),
                        value: t(
                          `${PRACTICE_SLUG.PLANT_PROTECTIONS}.${plantProtectionPracticesQuery.data?.find((p) => p.id === i.applicationId)?.translationSlug}`,
                          { ns: "practices" }
                        ),
                      },
                      {
                        label: t("cropPlantProtectionForm.question.amountAppliedQuestionLabel"),
                        value: `${i.amount} ${i.amountUnit}`,
                      },
                      {
                        label: t("cropPlantProtectionForm.question.activeIngredientsQuestionLabel"),
                        value: `${formatFractionDisplay(i.activeIngredientsPct ?? 0)} %`,
                      },
                    ]}
                  />
                ))
              : null,
          },
        ]}
      />
      {/* Crop irrigation form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        title={t("navigation.crop.irrigation")}
        titleButton={{ label: t("review.edit"), onClick: () => handleEditForm("irrigation") }}
        dataPairs={[
          {
            label: t("cropIrrigation.question.irrigation"),
            value: displayRadioAnswer(cropFootprintData?.footprint?.irrigationActive ?? null),
          },
          ...(cropFootprintData?.footprint?.irrigationActive
            ? [
                {
                  label: t("cropIrrigation.question.waterUsedQuestionLabel"),
                  value: `${cropFootprintData?.footprint?.waterUsedM3} m³`,
                },
              ]
            : []),
        ]}
      />
      {/* Crop drying form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        title={t("navigation.crop.drying")}
        titleButton={{ label: t("review.edit"), onClick: () => handleEditForm("drying") }}
        dataPairs={[
          {
            label: t("cropDryingForm.question.dryingQuestionLabel"),
            value: displayRadioAnswer(cropFootprintData?.footprint?.dryingActive ?? null),
          },
          ...(cropFootprintData?.footprint?.dryingActive
            ? [
                {
                  label: t("cropDryingForm.question.dryingTypeQuestionLabel"),
                  value: t(`cropDryingForm.dryingTypeOptions.${cropFootprintData?.footprint?.energyType}`),
                },
                {
                  label: t("cropDryingForm.question.fuelConsumptionQuestionLabel"),
                  value: `${cropFootprintData?.footprint?.fuelAmount} ${
                    cropFootprintData?.footprint?.energyType ? FARM_FOOTPRINTING_DRYING_ENERGY_TYPE_SUFFIXES[cropFootprintData.footprint.energyType] || "" : ""
                  }`,
                },
                {
                  label: t("cropDryingForm.question.renewableEnergyQuestionLabel"),
                  value: `${formatFractionInput(cropFootprintData?.footprint?.renewableEnergyPct)} %`,
                },
                {
                  label: t("cropDryingForm.question.moistureContentQuestionLabel"),
                  value: `${formatFractionInput(cropFootprintData?.footprint?.postDryingMoistureContentPct)} %`,
                },
                {
                  label: t("cropDryingForm.question.totalWeightQuestionLabel"),
                  value: `${cropFootprintData?.footprint?.driedWeightTonsPerHa} t/ha`,
                },
              ]
            : []),
        ]}
      />
      {/* Crop transport form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        title={t("navigation.crop.transport")}
        titleButton={{ label: t("review.edit"), onClick: () => handleEditForm("transport") }}
        dataPairs={[
          {
            label: t("cropTransportForm.question.radioQuestion"),
            value: displayRadioAnswer(cropFootprintData?.footprint?.cropSaleActive ?? null),
            children: cropFootprintData?.footprint?.cropSaleActive
              ? cropFootprintData?.footprint?.cropSales.map((i, index) => (
                  <ListCard
                    key={index}
                    isSublist
                    title={t("farmTransportForm.retailer", { count: index + 1 })}
                    dataPairs={[
                      {
                        label: t("cropTransportForm.question.purchaserNameQuestionLabel"),
                        value: i.purchaserName ?? "",
                      },
                      {
                        label: t("cropTransportForm.question.countryQuestionLabel"),
                        value: i.purchaserCountryCode ?? "",
                      },
                      {
                        label: t("cropTransportForm.question.postcodeQuestionLabel"),
                        value: i.purchaserPostCode ?? "",
                      },
                      {
                        label: t("cropTransportForm.question.shareQuestionLabel"),
                        value: `${formatFractionInput(i.soldYieldPct)} %`,
                      },
                    ]}
                  />
                ))
              : null,
          },
        ]}
      />
    </Card>
  );
};
